import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import { useAuth } from '@/contexts/AuthContext';
import { useSystemBarsStyle, systemBarsIconsForBackground } from '@/hooks/useSystemBarsStyle';
import { hostPaths } from '@/utils/hostPaths';
import communityService, { MyCommunityItem } from '@/services/communityService';
import { clientBookingService, BookingListItem } from '@/services/clientBookingService';
import {
  getClientProfile,
  getClientEventHistory,
  updateClientProfile,
  DataProfile,
  EventHistoryItem,
} from '@/services/clientProfile';
import { wilayahService, KotaOption } from '@/services/wilayahService';
import PhotoGalleryModal from '@/components/common/PhotoGalleryModal';
import { ALL_METRICS_LIST } from '@/pages/client/ClientStatsSettingsPage';
import { ALL_RACKET_METRICS_LIST } from '@/pages/client/ClientRacketStatsSettingsPage';
import mmrService, { ClientMMRProfile } from '@/services/mmrService';
import ClientRacketProfileView from '@/components/client/profile/ClientRacketProfileView';
import { getUploadErrorMessage } from '@/utils/apiErrorMessage';
import { MAX_GALLERY_COUNT, prepareGalleryFile, prepareGalleryFiles } from '@/utils/galleryImage';

const calculateAge = (birthDateStr?: string): string => {
  if (!birthDateStr) return '-';
  const birth = new Date(birthDateStr);
  if (isNaN(birth.getTime())) return '-';
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age > 0 ? `${age} tahun` : '-';
};

const ClientProfilePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCommunityUser = user?.role === 'community';
  useSystemBarsStyle(systemBarsIconsForBackground('light'));

  useEffect(() => {
    if (isCommunityUser) {
      navigate(hostPaths.profile.settings, { replace: true });
    }
  }, [isCommunityUser, navigate]);

  const [profile, setProfile] = useState<DataProfile | null>(null);
  const [mmrProfile, setMmrProfile] = useState<ClientMMRProfile | null>(null);
  const [profileType, setProfileType] = useState<'invasion' | 'racket'>('invasion');
  const [activeStatSport, setActiveStatSport] = useState<string>('minisoccer');
  const [activeHistoryTab, setActiveHistoryTab] = useState<'mabar' | 'sparing'>('mabar');

  // Profile Type Switch Modal State
  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState(false);
  const [targetProfileType, setTargetProfileType] = useState<'invasion' | 'racket' | null>(null);
  const [isSwitchingProfile, setIsSwitchingProfile] = useState(false);

  const handleTopSwitchClick = (type: 'invasion' | 'racket') => {
    if (type === profileType) return;
    setTargetProfileType(type);
    setIsSwitchModalOpen(true);
  };

  const handleConfirmSwitchProfile = async () => {
    if (!targetProfileType) return;
    try {
      setIsSwitchingProfile(true);
      await updateClientProfile({ profile_type: targetProfileType });
      setProfileType(targetProfileType);
      if (targetProfileType === 'racket') {
        setActiveStatSport('padel');
      } else {
        setActiveStatSport('minisoccer');
      }
      const label = targetProfileType === 'racket' ? 'Racket Game' : 'Invasion Game';
      showToast.success(`Profil ${label} kini tampil untuk pemain lain`);
      setIsSwitchModalOpen(false);
      setTargetProfileType(null);
      await fetchProfileData();
      await fetchMMRData();
    } catch (err: any) {
      showToast.error('Gagal memperbarui tipe profil');
    } finally {
      setIsSwitchingProfile(false);
    }
  };
  const [eventHistoryList, setEventHistoryList] = useState<EventHistoryItem[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [communities, setCommunities] = useState<MyCommunityItem[]>([]);
  const [, setRecentBookings] = useState<BookingListItem[]>([]);

  // Edit Info Pemain Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editPosition, setEditPosition] = useState<string>('Defender');
  const [editDominantFoot, setEditDominantFoot] = useState<string>('Kiri');
  const [editBirthDate, setEditBirthDate] = useState<string>('2002-03-14');
  const [editHeight, setEditHeight] = useState<string>('178');
  const [isSaving, setIsSaving] = useState(false);

  // Edit Info Dasar Modal State (Name, Username, Location)
  const [isEditBasicModalOpen, setIsEditBasicModalOpen] = useState(false);
  const [editName, setEditName] = useState<string>('');
  const [editUsername, setEditUsername] = useState<string>('');
  const [editLocationCode, setEditLocationCode] = useState<string>('');
  const [editLocationName, setEditLocationName] = useState<string>('Yogyakarta');
  const [locationSearchQuery, setLocationSearchQuery] = useState<string>('');
  const [locationOptions, setLocationOptions] = useState<KotaOption[]>([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [isSavingBasic, setIsSavingBasic] = useState(false);

  // Manage Gallery Modal State
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
  const [existingGallery, setExistingGallery] = useState<string[]>([]);
  const [newGalleryFiles, setNewGalleryFiles] = useState<File[]>([]);
  const [previewNewGallery, setPreviewNewGallery] = useState<string[]>([]);
  const [isSavingGallery, setIsSavingGallery] = useState(false);

  // Edit Profile Image Modal State
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [newAvatarFile, setNewAvatarFile] = useState<File | null>(null);
  const [previewAvatarUrl, setPreviewAvatarUrl] = useState<string>('');
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);

  // Gallery Preview Lightbox State
  const [galleryPreviewOpen, setGalleryPreviewOpen] = useState(false);
  const [galleryPreviewIndex, setGalleryPreviewIndex] = useState(0);

  const handlePhotoClick = (index: number) => {
    setGalleryPreviewIndex(index);
    setGalleryPreviewOpen(true);
  };

  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isLoadingCommunities, setIsLoadingCommunities] = useState(true);
  const [isLoadingMmr, setIsLoadingMmr] = useState(true);

  const fetchProfileData = async () => {
    try {
      setIsLoadingProfile(true);
      const res = await getClientProfile();
      if (res.success && res.data) {
        setProfile(res.data);
        if (res.data.profile_type && (res.data.profile_type === 'invasion' || res.data.profile_type === 'racket')) {
          setProfileType(res.data.profile_type);
          if (res.data.profile_type === 'racket' && activeStatSport !== 'padel' && activeStatSport !== 'badminton' && activeStatSport !== 'tennis') {
            setActiveStatSport('padel');
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch client profile:', err);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  useEffect(() => {
    if (isCommunityUser) {
      setIsLoadingCommunities(false);
      setIsLoadingProfile(false);
      return;
    }
    const fetchData = async () => {
      try {
        setIsLoadingCommunities(true);
        await fetchProfileData();
        const [commRes, bookingRes] = await Promise.allSettled([
          communityService.getMyCommunities(),
          clientBookingService.getClientBookings(1, 5, 'ended'),
        ]);

        if (commRes.status === 'fulfilled' && commRes.value.success) {
          setCommunities(commRes.value.data || []);
        }

        if (bookingRes.status === 'fulfilled' && bookingRes.value.success) {
          setRecentBookings(bookingRes.value.data.bookings || []);
        }
      } catch (err) {
        console.error('Failed to load profile data:', err);
      } finally {
        setIsLoadingCommunities(false);
      }
    };

    fetchData();
  }, [isCommunityUser]);

  const fetchMMRData = async () => {
    if (user?.role !== 'customer') {
      setIsLoadingMmr(false);
      return;
    }
    try {
      setIsLoadingMmr(true);
      const mmrRes = await mmrService.getClientMMRProfile(activeStatSport);
      if (mmrRes.success && mmrRes.data) {
        setMmrProfile(mmrRes.data);
      }
    } catch (e) {
      showToast.error('Gagal memuat profil MMR');
    } finally {
      setIsLoadingMmr(false);
    }
  };

  // Fetch MMR Profile when activeStatSport changes
  useEffect(() => {
    fetchMMRData();
  }, [activeStatSport]);

  // Debounced search for Wilayah / Kota
  useEffect(() => {
    if (!locationSearchQuery || locationSearchQuery.trim().length < 2) {
      setLocationOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearchingLocation(true);
      const res = await wilayahService.searchKota(locationSearchQuery);
      setLocationOptions(res);
      setIsSearchingLocation(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [locationSearchQuery]);

  // Fetch Event History based on Active History Tab & Profile Type
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setIsLoadingHistory(true);
        const gameCategory = profileType === 'racket' ? 'racket-game' : 'invasion-game';
        const res = await getClientEventHistory(activeHistoryTab, 3, gameCategory);
        if (res.success && res.data) {
          setEventHistoryList(res.data);
        } else {
          setEventHistoryList([]);
        }
      } catch (err) {
        console.error('Failed to fetch event history:', err);
        setEventHistoryList([]);
      } finally {
        setIsLoadingHistory(false);
      }
    };
    fetchHistory();
  }, [activeHistoryTab, profileType]);

  const openEditModal = () => {
    if (profileType === 'racket') {
      setEditPosition(profile?.information?.racket_position || profile?.information?.play_type || 'Ganda');
      setEditDominantFoot(profile?.information?.dominant_hand || 'Kanan');
    } else {
      setEditPosition(profile?.information?.position || 'Defender');
      setEditDominantFoot(profile?.information?.dominant_foot || 'Kiri');
    }
    setEditBirthDate(profile?.birthdate || '1998-03-14');
    setEditHeight(String(profile?.information?.height || '176'));
    setIsEditModalOpen(true);
  };

  const openEditBasicModal = () => {
    setEditName(profile?.name || user?.name || '');
    setEditUsername(profile?.username || user?.username || '');
    setEditLocationCode(profile?.information?.location_code || '');
    setEditLocationName(profile?.information?.location_name || 'Yogyakarta');
    setLocationSearchQuery(profile?.information?.location_name || 'Yogyakarta');
    setIsEditBasicModalOpen(true);
  };

  const openGalleryModal = () => {
    previewNewGallery.forEach((url) => URL.revokeObjectURL(url));
    setExistingGallery(galleryPhotos);
    setNewGalleryFiles([]);
    setPreviewNewGallery([]);
    setIsGalleryModalOpen(true);
  };

  const handleSelectNewGalleryFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const selected = input.files ? Array.from(input.files) : [];
    input.value = '';
    if (selected.length === 0) return;

    const result = await prepareGalleryFiles(selected, existingGallery.length + newGalleryFiles.length);
    if (result.error) {
      showToast.error(result.error);
    }
    if (result.files.length === 0) return;

    setNewGalleryFiles((prev) => [...prev, ...result.files]);
    const previews = result.files.map((file) => URL.createObjectURL(file));
    setPreviewNewGallery((prev) => [...prev, ...previews]);
  };

  const handleRemoveExistingPhoto = (index: number) => {
    setExistingGallery((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleRemoveNewPhoto = (index: number) => {
    setPreviewNewGallery((prev) => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, idx) => idx !== index);
    });
    setNewGalleryFiles((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSaveInfoPemain = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      const updateData: any = {
        birth_date: editBirthDate,
        information: {
          ...(profile?.information || {}),
          height: Number(editHeight) || editHeight,
        },
      };

      if (profileType === 'racket') {
        updateData.information.racket_position = editPosition;
        updateData.information.play_type = editPosition;
        updateData.information.dominant_hand = editDominantFoot;
      } else {
        updateData.information.position = editPosition;
        updateData.information.dominant_foot = editDominantFoot;
      }

      await updateClientProfile(updateData);
      showToast.success('Info pemain berhasil disimpan!');
      setIsEditModalOpen(false);
      await fetchProfileData();
      await fetchMMRData();
    } catch (err: any) {
      showToast.error(err?.response?.data?.message || 'Gagal menyimpan info pemain');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveInfoDasar = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSavingBasic(true);
      await updateClientProfile({
        name: editName,
        username: editUsername,
        information: {
          location_code: editLocationCode,
          location_name: editLocationName,
        },
      });
      showToast.success('Info dasar berhasil disimpan!');
      setIsEditBasicModalOpen(false);
      await fetchProfileData();
    } catch (err: any) {
      showToast.error(err?.response?.data?.message || 'Gagal menyimpan info dasar');
    } finally {
      setIsSavingBasic(false);
    }
  };

  const handleSaveGallery = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSavingGallery(true);
      await updateClientProfile({
        keepGallery: existingGallery,
        galleryFiles: newGalleryFiles,
      });
      showToast.success('Galeri berhasil diperbarui!');
      setIsGalleryModalOpen(false);
      await fetchProfileData();
    } catch (err: unknown) {
      showToast.error(getUploadErrorMessage(err, 'Gagal menyimpan galeri foto. Coba lagi.'));
    } finally {
      setIsSavingGallery(false);
    }
  };

  const openAvatarModal = () => {
    setNewAvatarFile(null);
    setPreviewAvatarUrl(avatarUrl);
    setIsAvatarModalOpen(true);
  };

  const handleSelectAvatarFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    try {
      const prepared = await prepareGalleryFile(file);
      setNewAvatarFile(prepared);
      setPreviewAvatarUrl(URL.createObjectURL(prepared));
    } catch (err: unknown) {
      showToast.error(err instanceof Error ? err.message : 'Gagal membaca foto profil');
    }
  };

  const handleSaveAvatar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAvatarFile) {
      showToast.error('Pilih foto baru terlebih dahulu');
      return;
    }
    try {
      setIsSavingAvatar(true);
      await updateClientProfile({
        profileImageFile: newAvatarFile,
      });
      showToast.success('Foto profil berhasil diperbarui!');
      setIsAvatarModalOpen(false);
      await fetchProfileData();
    } catch (err: unknown) {
      showToast.error(getUploadErrorMessage(err, 'Gagal memperbarui foto profil. Coba lagi.'));
    } finally {
      setIsSavingAvatar(false);
    }
  };

  const displayName = profile?.name || user?.name || user?.username || 'Alfir Dausihaq';
  const username = profile?.username ? `@${profile.username}` : user?.username ? `@${user.username}` : '@alfirdausihaq';
  const avatarUrl = profile?.profile_image || user?.avatar || '';

  const positionDisplay = profileType === 'racket'
    ? (profile?.information?.racket_position || profile?.information?.position || '-')
    : (profile?.information?.position || '-');
  const heightDisplay = profile?.information?.height ? `${profile.information.height} cm` : '-';
  const dominantFootDisplay = profileType === 'racket'
    ? (profile?.information?.dominant_hand || profile?.information?.dominant_foot || '-')
    : (profile?.information?.dominant_foot || '-');
  const ageDisplay = calculateAge(profile?.birthdate);
  const locationDisplay = profile?.information?.location_name || '-';

  // Real Stats per sport from API
  const sportStatItem = profile?.sport_stats?.[activeStatSport];

  const activeMetricsList = profileType === 'racket' ? ALL_RACKET_METRICS_LIST : ALL_METRICS_LIST;
  const validMetricKeys = activeMetricsList.map((m) => m.key);
  const defaultFallbackKeys = profileType === 'racket'
    ? ['poin', 'point_loss', 'total_menang', 'total_kalah']
    : ['gol', 'assist', 'save', 'foul'];

  const rawConfigKeys: string[] =
    profile?.information?.stat_config?.[activeStatSport] ||
    JSON.parse(localStorage.getItem(`sporto_stat_config_${activeStatSport}`) || 'null') ||
    defaultFallbackKeys;

  const configuredMetricKeys: string[] = (() => {
    const filtered = (rawConfigKeys || []).filter((k) => validMetricKeys.includes(k));
    if (filtered.length === 4) return filtered;
    const res = [...filtered];
    for (const dKey of defaultFallbackKeys) {
      if (res.length >= 4) break;
      if (!res.includes(dKey)) res.push(dKey);
    }
    return res;
  })();

  // Gallery photos from player information or assets JSONB
  const rawGallery = profile?.information?.gallery || profile?.assets?.gallery || [];
  const galleryPhotos = (Array.isArray(rawGallery) ? rawGallery : []).filter(
    (url): url is string =>
      typeof url === 'string' &&
      url.trim() !== '' &&
      url !== '[]' &&
      url !== 'null' &&
      url !== 'undefined'
  );

  const HASIL_SPORTS = ['futsal', 'mini-soccer', 'minisoccer', 'sepak-bola', 'sepakbola', 'football', 'padel', 'badminton'];

  const handleEventDetailClick = (item: EventHistoryItem) => {
    const sportSlug = (item.sport_category_slug || '').toLowerCase();
    if (HASIL_SPORTS.includes(sportSlug)) {
      navigate(`/event/${item.slug}/statistik`);
    } else {
      navigate(`/event/${item.slug}`);
    }
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen text-sporto-dark pb-5">
      {/* FIXED TOPBAR HEADER FOR PROFILE TYPE SWITCH (Invasion Game vs Racket Game) */}
      <div className="fixed top-0 left-0 right-0 md:sticky md:top-0 z-40 bg-[#F8FAFC] pt-safe md:pt-3 pb-2.5 px-4 shadow-xs border-b border-gray-200/40">
        <div className="max-w-2xl mx-auto pt-2 md:pt-0">
          <div
            className={`p-1.5 rounded-full flex items-center justify-between gap-1 transition-all ${
              profileType === 'racket'
                ? 'bg-[#ECF2FE] border border-[#DBE6FE]'
                : 'bg-[#F4F5F7] border border-gray-200/50'
            }`}
          >
            <button
              type="button"
              onClick={() => handleTopSwitchClick('invasion')}
              className={`flex-1 py-2.5 px-4 rounded-full text-sm font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                profileType === 'invasion'
                  ? 'bg-white text-[#FC480D] shadow-xs'
                  : 'text-gray-500 hover:text-gray-800 font-semibold'
              }`}
            >
              <Icon
                icon="ph:soccer-ball-bold"
                className={`w-4.5 h-4.5 ${profileType === 'invasion' ? 'text-[#FC480D]' : 'text-gray-400'}`}
              />
              <span>Invasion Game</span>
            </button>

            <button
              type="button"
              onClick={() => handleTopSwitchClick('racket')}
              className={`flex-1 py-2.5 px-4 rounded-full text-sm font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                profileType === 'racket'
                  ? 'bg-white text-[#1D4ED8] shadow-xs'
                  : 'text-gray-500 hover:text-gray-800 font-semibold'
              }`}
            >
              <Icon
                icon="ph:tennis-ball-bold"
                className={`w-4.5 h-4.5 ${profileType === 'racket' ? 'text-[#1D4ED8]' : 'text-gray-400'}`}
              />
              <span>Racket Game</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-20 md:pt-5 space-y-5">
        {/* PROFILE TYPE CONDITIONAL RENDER: RACKET GAME vs INVASION GAME */}
        {profileType === 'racket' ? (
          <ClientRacketProfileView
            profile={profile}
            avatarUrl={avatarUrl}
            displayName={displayName}
            username={username}
            locationDisplay={locationDisplay}
            ageDisplay={ageDisplay}
            dominantFootDisplay={dominantFootDisplay}
            heightDisplay={heightDisplay}
            activeStatSport={activeStatSport}
            setActiveStatSport={setActiveStatSport}
            activeHistoryTab={activeHistoryTab}
            setActiveHistoryTab={setActiveHistoryTab}
            galleryPhotos={galleryPhotos}
            communities={communities}
            isLoadingCommunities={isLoadingCommunities}
            eventHistoryList={eventHistoryList}
            isLoadingHistory={isLoadingHistory}
            openAvatarModal={openAvatarModal}
            openEditBasicModal={openEditBasicModal}
            openEditModal={openEditModal}
            openGalleryModal={openGalleryModal}
            handlePhotoClick={handlePhotoClick}
            handleEventDetailClick={handleEventDetailClick}
            onProfileUpdate={fetchProfileData}
          />
        ) : (
          /* ========================================================= */
          /* INVASION GAME VIEW (ORANGE & SLATE THEME)                */
          /* ========================================================= */
          <>
            {/* 1. HERO COVER & PROFILE IMAGE CARD */}
        {isLoadingProfile ? (
          <div className="bg-white rounded-3xl border border-[#E0E6ED] shadow-2xs overflow-hidden">
            <div className="relative w-full aspect-square md:aspect-[4/3] rounded-t-3xl bg-gray-200 animate-pulse flex flex-col justify-between p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-full bg-gray-300/80" />
                <div className="w-10 h-10 rounded-full bg-gray-300/80" />
              </div>
              <div className="space-y-3">
                <div className="h-7 w-48 bg-gray-300/80 rounded-xl" />
                <div className="flex items-center gap-3">
                  <div className="h-4 w-20 bg-gray-300/70 rounded-lg" />
                  <div className="h-4 w-24 bg-gray-300/70 rounded-lg" />
                  <div className="h-4 w-24 bg-gray-300/70 rounded-lg" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 divide-x divide-gray-100 p-4 text-center">
              <div>
                <div className="h-3 w-12 bg-gray-200 rounded animate-pulse mx-auto mb-2" />
                <div className="h-6 w-10 bg-gray-200 rounded animate-pulse mx-auto" />
              </div>
              <div>
                <div className="h-3 w-14 bg-gray-200 rounded animate-pulse mx-auto mb-2" />
                <div className="h-6 w-10 bg-gray-200 rounded animate-pulse mx-auto" />
              </div>
              <div>
                <div className="h-3 w-14 bg-gray-200 rounded animate-pulse mx-auto mb-2" />
                <div className="h-6 w-12 bg-gray-200 rounded animate-pulse mx-auto" />
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-[#E0E6ED] shadow-2xs overflow-hidden">
            {/* Main Cover Photo Box */}
            <div
              className="relative w-full aspect-square md:aspect-[4/3] rounded-t-3xl bg-slate-900 bg-cover bg-center overflow-hidden flex flex-col justify-between p-4 md:p-6 text-white"
              style={{ backgroundImage: `url("${avatarUrl}")` }}
            >
              {/* Dark Overlay Gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/30 pointer-events-none" />

              {/* Top Row Action Buttons */}
              <div className="relative z-10 flex items-center justify-between">
                {/* Camera Button on the Left */}
                <button
                  type="button"
                  onClick={openAvatarModal}
                  className="w-11 h-11 rounded-2xl bg-white/20 hover:bg-white/30 text-white transition-all flex items-center justify-center cursor-pointer backdrop-blur-md border border-white/20 shadow-sm"
                  aria-label="Ubah Foto Profil"
                >
                  <Icon icon="ph:camera-bold" className="w-5 h-5 text-white" />
                </button>

                {/* Top Right Menu Button */}
                <button
                  type="button"
                  onClick={() => navigate('/profile/account')}
                  className="w-11 h-11 rounded-2xl bg-white/20 hover:bg-white/30 text-white transition-all flex items-center justify-center cursor-pointer backdrop-blur-md border border-white/20 shadow-sm"
                  aria-label="Pengaturan Akun"
                >
                  <Icon icon="ph:list-bold" className="w-5.5 h-5.5 text-white" />
                </button>
              </div>

              {/* Bottom Hero Info */}
              <div className="relative z-10 space-y-2.5">
                {/* Leaderboard Rank Badge Pill (e.g. 🏆 #5 Defender >) */}
                {mmrProfile?.rankDisplay && (
                  <div>
                    <button
                      type="button"
                      onClick={() => navigate('/mmr/leaderboard')}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-[#F5D680] text-[#5C4405] text-xs font-bold hover:bg-[#F0C755] transition-all shadow-md cursor-pointer border border-[#E9C35C]/60"
                    >
                      <Icon icon="ph:trophy-bold" className="w-3.5 h-3.5 text-[#5C4405]" />
                      <span>{mmrProfile.rankDisplay}</span>
                      <Icon icon="ph:caret-right-bold" className="w-3 h-3 text-[#5C4405]" />
                    </button>
                  </div>
                )}

                {/* Name & Pencil */}
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl md:text-3xl font-bold text-white leading-tight">
                    {displayName}
                  </h1>
                  <button
                    type="button"
                    onClick={openEditBasicModal}
                    className="w-7 h-7 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white hover:bg-white/30 transition-colors cursor-pointer"
                  >
                    <Icon icon="ph:pencil-simple-bold" className="w-4 h-4" />
                  </button>
                </div>

                {/* Username, Position & City */}
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-200 font-medium">
                  <span>{username}</span>
                  <span>·</span>
                  <span>{positionDisplay}</span>
                  <span>·</span>
                  <span className="flex items-center gap-1">
                    <Icon icon="ph:map-pin-bold" className="w-3.5 h-3.5 text-gray-300" />
                    {locationDisplay}
                  </span>
                </div>
              </div>
            </div>

            {/* Stat Summary Bar (Attached below Cover) */}
            <div className="grid grid-cols-3 divide-x divide-gray-100 p-4 text-center">
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 block mb-0.5">
                  EVENTS
                </span>
                <span className="text-xl md:text-2xl font-bold text-[#0E1726]">
                  {profile?.stats?.events ?? 0}
                </span>
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 block mb-0.5">
                  MATCHES
                </span>
                <span className="text-xl md:text-2xl font-bold text-[#0E1726]">
                  {profile?.stats?.matches ?? 0}
                </span>
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 block mb-0.5">
                  WINRATE
                </span>
                <span className="text-xl md:text-2xl font-bold text-[#FC480D]">
                  {profile?.stats?.win_rate !== undefined ? `${profile.stats.win_rate}%` : '0%'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* MMR TIER CARD (Dark Card Layout `#0F172A`) */}
        {isLoadingMmr ? (
          <div className="bg-[#0F172A] rounded-3xl p-5 md:p-6 text-white shadow-xl space-y-4 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-slate-800/80" />
                <div className="space-y-1.5">
                  <div className="h-2.5 w-20 bg-slate-800/80 rounded" />
                  <div className="h-6 w-24 bg-slate-700/80 rounded-md" />
                </div>
              </div>

              <div className="text-right space-y-1">
                <div className="h-7 w-24 bg-slate-700/80 rounded-md ml-auto" />
                <div className="h-3 w-8 bg-slate-800/80 rounded ml-auto" />
              </div>
            </div>

            {/* Progress Bar Skeleton */}
            <div className="space-y-2 pt-1">
              <div className="w-full h-2.5 bg-slate-800/80 rounded-full" />
              <div className="flex items-center justify-between text-xs pt-0.5">
                <div className="h-3 w-36 bg-slate-800/80 rounded" />
                <div className="h-3 w-20 bg-slate-800/80 rounded" />
              </div>
            </div>

            <div className="border-t border-slate-800 pt-3 flex items-center justify-between text-xs">
              <div className="h-4 w-40 bg-slate-800/80 rounded" />
              <div className="h-4 w-16 bg-slate-800/80 rounded" />
            </div>
          </div>
        ) : (
          <div
            onClick={() => navigate('/profile/mmr')}
            className="bg-[#0F172A] rounded-3xl p-5 md:p-6 text-white shadow-xl space-y-4 cursor-pointer hover:scale-[1.005] hover:bg-[#131D33] active:scale-[0.995] transition-all border border-slate-800/80"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center backdrop-blur-sm border border-white/10">
                  <Icon icon="ph:shield-bold" className="w-6 h-6 text-slate-300" />
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold tracking-widest text-slate-400 block">
                    CURRENT TIER
                  </span>
                  <h2 className="text-xl font-bold text-white leading-tight">
                    {mmrProfile?.tier || 'Bronze'}
                  </h2>
                </div>
              </div>

              <div className="text-right">
                <div className="flex items-baseline justify-end gap-1">
                  <span className="text-2xl md:text-3xl font-extrabold text-white">
                    {Math.round(mmrProfile?.mmr ?? 0).toLocaleString('id-ID')}
                  </span>
                  <span className="text-xs font-semibold text-slate-400">MMR</span>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="space-y-1.5 pt-1">
              <div className="w-full h-2.5 bg-slate-800 rounded-full overflow-hidden p-0.5">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full transition-all duration-500"
                  style={{
                    width: `${
                      mmrProfile?.targetMMR
                        ? Math.min(100, Math.max(0, ((mmrProfile.mmr / mmrProfile.targetMMR) * 100)))
                        : 0
                    }%`,
                  }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-400 pt-0.5 font-medium">
                <span>
                  {mmrProfile?.neededMMR !== undefined && mmrProfile?.neededMMR !== null
                    ? `${Math.round(mmrProfile.neededMMR).toLocaleString('id-ID')} MMR ke tier berikutnya`
                    : '0 MMR ke tier berikutnya'}
                </span>
                <span className="text-slate-300">
                  {mmrProfile?.nextTier || 'Silver'} · {Math.round(mmrProfile?.targetMMR ?? 2000).toLocaleString('id-ID')}
                </span>
              </div>
            </div>

            <div className="border-t border-slate-800 pt-3 flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5 text-emerald-400 font-semibold">
                <Icon icon="ph:trend-up-bold" className="w-4 h-4" />
                <span>
                  {(mmrProfile?.delta30Days ?? 0) >= 0
                    ? `+${(mmrProfile?.delta30Days ?? 0).toLocaleString('id-ID')}`
                    : (mmrProfile?.delta30Days ?? 0).toLocaleString('id-ID')}{' '}
                  MMR 30 hari terakhir
                </span>
              </div>
              <div className="text-slate-300 font-medium flex items-center gap-1">
                <span>Detail MMR</span>
                <Icon icon="ph:caret-right-bold" className="w-3.5 h-3.5 text-slate-300" />
              </div>
            </div>
          </div>
        )}

        {/* 2. INFO PEMAIN */}
        {isLoadingProfile ? (
          <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
            <div className="flex items-center justify-between">
              <div className="h-5 w-28 bg-gray-200 rounded-lg animate-pulse" />
              <div className="h-8 w-16 bg-gray-200 rounded-full animate-pulse" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className=" rounded-2xl p-3.5 space-y-2 animate-pulse">
                  <div className="h-3 w-20 bg-gray-200 rounded" />
                  <div className="h-5 w-24 bg-gray-300 rounded-md" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[#0E1726]">Info pemain</h3>
              <button
                type="button"
                onClick={openEditModal}
                className="h-8 px-3.5 rounded-full bg-[#FFF2EE] text-[#FC480D] hover:bg-orange-100 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Icon icon="ph:pencil-simple-bold" className="w-3.5 h-3.5" />
                <span>Edit</span>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* Posisi utama */}
              <div className=" rounded-2xl p-3.5 space-y-1">
                <span className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                  <Icon icon="ph:map-pin" className="w-3.5 h-3.5 text-gray-400" />
                  Posisi utama
                </span>
                <span className="text-sm font-bold text-[#0E1726] block">{positionDisplay}</span>
              </div>

              {/* Tinggi badan */}
              <div className=" rounded-2xl p-3.5 space-y-1">
                <span className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                  <Icon icon="ph:arrows-out-cardinal" className="w-3.5 h-3.5 text-gray-400" />
                  Tinggi badan
                </span>
                <span className="text-sm font-bold text-[#0E1726] block">{heightDisplay}</span>
              </div>

              {/* Kaki dominan */}
              <div className=" rounded-2xl p-3.5 space-y-1">
                <span className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                  <Icon icon="ph:sneaker" className="w-3.5 h-3.5 text-gray-400" />
                  Kaki dominan
                </span>
                <span className="text-sm font-bold text-[#0E1726] block">{dominantFootDisplay}</span>
              </div>

              {/* Umur */}
              <div className=" rounded-2xl p-3.5 space-y-1">
                <span className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                  <Icon icon="ph:clock" className="w-3.5 h-3.5 text-gray-400" />
                  Umur
                </span>
                <span className="text-sm font-bold text-[#0E1726] block">{ageDisplay}</span>
              </div>
            </div>
          </div>
        )}

        {/* 3. KOMUNITAS */}
        {isLoadingCommunities ? (
          <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
            <div className="flex items-center justify-between">
              <div className="h-5 w-24 bg-gray-200 rounded-lg animate-pulse" />
              <div className="h-8 w-20 bg-gray-200 rounded-full animate-pulse" />
            </div>
            <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-none">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex flex-col items-center gap-2 shrink-0 w-16 animate-pulse">
                  <div className="w-14 h-14 rounded-full bg-gray-200" />
                  <div className="h-3 w-12 bg-gray-200 rounded" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[#0E1726]">Komunitas</h3>
              <button
                type="button"
                onClick={() => navigate('/my-community')}
                className="h-8 px-3.5 rounded-full bg-amber-50 text-amber-600 hover:bg-amber-100 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Icon icon="ph:sliders-horizontal" className="w-3.5 h-3.5" />
                <span>Manage</span>
              </button>
            </div>

            {communities.length > 0 ? (
              <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-none">
                {communities.map((comm) => {
                  const initial = comm.name ? comm.name.substring(0, 2).toUpperCase() : 'KM';
                  return (
                    <div
                      key={comm.slug}
                      onClick={() => navigate(`/community/${comm.slug}`)}
                      className="flex flex-col items-center gap-1.5 shrink-0 w-16 text-center cursor-pointer group"
                    >
                      {comm.logo ? (
                        <div className="w-14 h-14 rounded-full border-2 border-[#FC480D] overflow-hidden shadow-2xs group-hover:scale-105 transition-transform bg-gray-100">
                          <img src={comm.logo} alt={comm.name} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-14 h-14 rounded-full border-2 border-[#FC480D] bg-[#FFF2EE] flex items-center justify-center text-sm font-bold text-[#FC480D] shadow-2xs group-hover:scale-105 transition-transform">
                          {initial}
                        </div>
                      )}
                      <span className="text-[11px] font-semibold text-gray-600 leading-tight truncate w-full">
                        {comm.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-6 px-4 rounded-2xl border-2 border-dashed border-gray-200 text-center flex flex-col items-center justify-center space-y-3 bg-gray-50/50">
                <div className="w-12 h-12 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center">
                  <Icon icon="ph:users-three-bold" className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-[#0E1726]">Belum bergabung ke komunitas</h4>
                  <p className="text-xs text-gray-400 max-w-xs">
                    Temukan dan bergabunglah dengan komunitas olahraga favoritmu di sekitarmu!
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/discovery/community')}
                  className="h-8 px-4 rounded-full bg-[#FC480D] hover:bg-[#D83B08] text-white text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs mt-1"
                >
                  <Icon icon="ph:plus-bold" className="w-3.5 h-3.5" />
                  <span>Cari komunitas</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* 4. RINGKASAN STATISTIK */}
        {isLoadingProfile ? (
          <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
            <div className="flex items-center justify-between">
              <div className="h-5 w-36 bg-gray-200 rounded-lg animate-pulse" />
            </div>
            <div className="bg-[#F5F6F7] p-1 rounded-2xl flex items-center justify-between gap-1">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex-1 h-8 bg-gray-200 rounded-xl animate-pulse" />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white border border-[#E0E6ED] rounded-2xl p-3.5 flex items-center gap-3 shadow-2xs animate-pulse">
                  <div className="w-9 h-9 rounded-xl bg-gray-200 shrink-0" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-5 w-8 bg-gray-200 rounded" />
                    <div className="h-3 w-10 bg-gray-200 rounded" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[#0E1726]">Ringkasan statistik</h3>
                <button
                  type="button"
                  onClick={() => navigate('/profile/stats')}
                  className="h-8 px-3.5 rounded-full bg-amber-50 text-amber-600 hover:bg-amber-100 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  <Icon icon="ph:sliders-horizontal" className="w-3.5 h-3.5" />
                  <span>Manage</span>
                </button>
            </div>

            {/* Sport Selector Pills */}
            <div className="bg-[#F5F6F7] p-1 rounded-2xl flex items-center justify-between gap-1 text-xs font-semibold text-gray-500">
              {profileType === 'invasion' ? (
                <>
                  <button
                    type="button"
                    onClick={() => setActiveStatSport('minisoccer')}
                    className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                      activeStatSport === 'minisoccer'
                        ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                        : 'hover:text-[#0E1726]'
                    }`}
                  >
                    Mini soccer
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveStatSport('sepakbola')}
                    className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                      activeStatSport === 'sepakbola'
                        ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                        : 'hover:text-[#0E1726]'
                    }`}
                  >
                    Sepak bola
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveStatSport('futsal')}
                    className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                      activeStatSport === 'futsal'
                        ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                        : 'hover:text-[#0E1726]'
                    }`}
                  >
                    Futsal
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setActiveStatSport('padel')}
                    className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                      activeStatSport === 'padel'
                        ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                        : 'hover:text-[#0E1726]'
                    }`}
                  >
                    Padel
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveStatSport('badminton')}
                    className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                      activeStatSport === 'badminton'
                        ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                        : 'hover:text-[#0E1726]'
                    }`}
                  >
                    Badminton
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveStatSport('tennis')}
                    className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                      activeStatSport === 'tennis'
                        ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                        : 'hover:text-[#0E1726]'
                    }`}
                  >
                    Tennis
                  </button>
                </>
              )}
            </div>

            {/* 2x2 Grid Stats */}
            <div className="grid grid-cols-2 gap-3">
              {configuredMetricKeys.map((mKey) => {
                const metricDef = activeMetricsList.find((m) => m.key === mKey);
                if (!metricDef) return null;
                const val = metricDef.getValue(sportStatItem);

                return (
                  <div
                    key={mKey}
                    className="bg-white border border-[#E0E6ED] rounded-2xl p-3.5 flex items-center gap-3 shadow-2xs"
                  >
                    <div className={`w-9 h-9 rounded-xl ${metricDef.bg} ${metricDef.text} flex items-center justify-center shrink-0`}>
                      <Icon icon={metricDef.icon} className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="text-xl font-bold text-[#0E1726] block">
                        {val}
                      </span>
                      <span className="text-xs text-gray-400 font-medium">{metricDef.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 5. RIWAYAT EVENT */}
        <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
          <h3 className="text-base font-bold text-[#0E1726]">Riwayat event</h3>

          {/* Tabs */}
          <div className="bg-[#F5F6F7] p-1 rounded-2xl flex items-center justify-between gap-1 text-xs font-semibold text-gray-500">
            <button
              type="button"
              onClick={() => setActiveHistoryTab('mabar')}
              className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                activeHistoryTab === 'mabar'
                  ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                  : 'hover:text-[#0E1726]'
              }`}
            >
              Mabar
            </button>
            <button
              type="button"
              onClick={() => setActiveHistoryTab('sparing')}
              className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                activeHistoryTab === 'sparing'
                  ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                  : 'hover:text-[#0E1726]'
              }`}
            >
              Sparing
            </button>
          </div>

          {/* List of History Items */}
          <div className="divide-y divide-gray-100 space-y-2">
            {isLoadingHistory ? (
              <div className="space-y-3 pt-1">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center justify-between gap-3 animate-pulse pt-2">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-2xl bg-gray-200 shrink-0" />
                      <div className="space-y-1.5 flex-1 min-w-0">
                        <div className="h-4 w-32 bg-gray-200 rounded" />
                        <div className="h-3 w-44 bg-gray-200 rounded" />
                      </div>
                    </div>
                    <div className="h-8 w-16 rounded-full bg-gray-200 shrink-0" />
                  </div>
                ))}
              </div>
            ) : eventHistoryList.length === 0 ? (
              <div className="py-6 text-center text-xs text-gray-400 font-medium">
                Belum ada riwayat event {activeHistoryTab === 'sparing' ? 'sparing' : 'mabar'}.
              </div>
            ) : (
              eventHistoryList.map((item) => {
                const dateStr = item.event_date
                  ? new Date(item.event_date).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : '';
                const locationStr = item.lokasi_arena || item.city || '';
                const subtitle = [locationStr, item.city !== locationStr ? item.city : '', dateStr]
                  .filter(Boolean)
                  .join(' · ');

                return (
                  <div key={item.id} className="pt-2 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {item.host_avatar ? (
                        <img
                          src={item.host_avatar}
                          alt={item.host_name || item.name}
                          className="w-10 h-10 rounded-2xl object-cover shrink-0 border border-gray-100 shadow-2xs"
                        />
                      ) : (
                        <div
                          className={`w-10 h-10 rounded-2xl flex items-center justify-center font-bold text-xs shrink-0 ${
                            item.badge_bg || 'bg-slate-900 text-white'
                          }`}
                        >
                          {item.host_name ? item.host_name.charAt(0).toUpperCase() : (item.badge || 'MB')}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h4 className="text-xs font-bold text-[#0E1726] truncate">{item.name}</h4>
                        <p className="text-[11px] text-gray-400 truncate mt-0.5">{subtitle}</p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleEventDetailClick(item)}
                      className="h-8 px-3 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-600 text-xs font-semibold flex items-center gap-1 transition-colors cursor-pointer shrink-0"
                    >
                      <span>Detail</span>
                      <Icon icon="ph:caret-right-bold" className="w-3 h-3" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* 6. GALERI */}
        {isLoadingProfile ? (
          <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
            <div className="flex items-center justify-between">
              <div className="h-5 w-20 bg-gray-200 rounded-lg animate-pulse" />
              <div className="h-8 w-20 bg-gray-200 rounded-full animate-pulse" />
            </div>
            <div className="grid grid-cols-3 gap-2.5">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-square rounded-2xl bg-gray-200 animate-pulse" />
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[#0E1726]">Galeri</h3>
              <button
                type="button"
                onClick={openGalleryModal}
                className="h-8 px-3.5 rounded-full bg-amber-50 text-amber-600 hover:bg-amber-100 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Icon icon="ph:sliders-horizontal" className="w-3.5 h-3.5" />
                <span>Manage</span>
              </button>
            </div>

            {galleryPhotos.length > 0 ? (
              <div className="grid grid-cols-3 gap-2.5">
                {galleryPhotos.map((photo, idx) => (
                  <div
                    key={idx}
                    onClick={() => handlePhotoClick(idx)}
                    className="aspect-square rounded-2xl overflow-hidden bg-gray-100 border border-gray-100 hover:opacity-90 transition-opacity cursor-pointer"
                  >
                    <img src={photo} alt={`Galeri ${idx + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 px-4 rounded-2xl border-2 border-dashed border-gray-200 text-center flex flex-col items-center justify-center space-y-3 bg-gray-50/50">
                <div className="w-14 h-14 rounded-full bg-orange-50 text-[#FC480D] flex items-center justify-center">
                  <Icon icon="ph:image-square-bold" className="w-7 h-7" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-[#0E1726]">Belum ada foto galeri</h4>
                  <p className="text-xs text-gray-400 max-w-xs">
                    Tunjukkan momen terbaikmu di lapangan dengan menambahkan foto ke galeri profil.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={openGalleryModal}
                  className="h-9 px-4 rounded-full bg-[#FC480D] hover:bg-[#D83B08] text-white text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs mt-1"
                >
                  <Icon icon="ph:plus-bold" className="w-3.5 h-3.5" />
                  <span>Tambah foto</span>
                </button>
              </div>
            )}
          </div>
        )}
      </>
    )}
  </div>

      {/* MANAGE GALLERY MODAL */}
      {isGalleryModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col animate-in slide-in-from-bottom duration-300">
            {/* Header */}
            <div className="px-5 py-4 border-b border-[#E0E6ED] flex items-center justify-between bg-white sticky top-0 z-10">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIsGalleryModalOpen(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-[#0E1726] hover:bg-gray-100 cursor-pointer transition-colors"
                >
                  <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
                </button>
                <h2 className="text-base font-bold text-[#0E1726]">Kelola Galeri Foto</h2>
              </div>
              <span className="text-xs text-gray-400 font-medium">
                {existingGallery.length + newGalleryFiles.length}/6 Foto
              </span>
            </div>

            {/* Form Body */}
            <form onSubmit={handleSaveGallery} className="p-5 overflow-y-auto space-y-5 flex-1">
              {/* Existing Photos Grid */}
              {existingGallery.length > 0 && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-500 block">Foto Tersimpan</label>
                  <div className="grid grid-cols-3 gap-2.5">
                    {existingGallery.map((url, idx) => (
                      <div key={idx} className="relative aspect-square rounded-2xl overflow-hidden group border border-gray-200">
                        <img src={url} alt={`Saved ${idx}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => handleRemoveExistingPhoto(idx)}
                          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md hover:scale-110 transition-transform cursor-pointer"
                        >
                          <Icon icon="ph:x-bold" className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Preview Photos Grid */}
              {previewNewGallery.length > 0 && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-500 block">Foto Baru (Belum Disimpan)</label>
                  <div className="grid grid-cols-3 gap-2.5">
                    {previewNewGallery.map((url, idx) => (
                      <div key={idx} className="relative aspect-square rounded-2xl overflow-hidden group border border-amber-300">
                        <img src={url} alt={`New ${idx}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => handleRemoveNewPhoto(idx)}
                          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md hover:scale-110 transition-transform cursor-pointer"
                        >
                          <Icon icon="ph:x-bold" className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add New Photos Upload Dropzone */}
              {existingGallery.length + newGalleryFiles.length < MAX_GALLERY_COUNT && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-500 block">Upload Foto Baru</label>
                  <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 hover:border-[#FC480D] rounded-2xl cursor-pointer bg-gray-50 hover:bg-orange-50/50 transition-colors">
                    <Icon icon="ph:cloud-arrow-up-bold" className="w-8 h-8 text-[#FC480D] mb-1" />
                    <span className="text-xs font-bold text-[#0E1726]">Klik untuk pilih foto</span>
                    <span className="text-[11px] text-gray-400 mt-0.5">
                      JPG/PNG/WebP, maksimal {MAX_GALLERY_COUNT} foto. Foto kamera dikompres otomatis.
                    </span>
                    <input
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/webp,image/*"
                      onChange={handleSelectNewGalleryFiles}
                      className="hidden"
                    />
                  </label>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-3">
                <button
                  type="submit"
                  disabled={isSavingGallery}
                  className="w-full h-12 rounded-full bg-[#FC480D] hover:bg-[#D83B08] disabled:opacity-50 text-white font-bold text-sm shadow-md transition-all active:scale-98 cursor-pointer flex items-center justify-center gap-2"
                >
                  {isSavingGallery ? (
                    <>
                      <Icon icon="ph:spinner-gap-bold" className="w-5 h-5 animate-spin" />
                      <span>Menyimpan...</span>
                    </>
                  ) : (
                    <span>Simpan galeri</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT INFO DASAR MODAL (NAME, USERNAME, LOCATION) */}
      {isEditBasicModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col animate-in slide-in-from-bottom duration-300">
            {/* Header */}
            <div className="px-5 py-4 border-b border-[#E0E6ED] flex items-center gap-3 bg-white sticky top-0 z-10">
              <button
                type="button"
                onClick={() => setIsEditBasicModalOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-[#0E1726] hover:bg-gray-100 cursor-pointer transition-colors"
              >
                <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
              </button>
              <h2 className="text-base font-bold text-[#0E1726]">Edit Info Dasar</h2>
            </div>

            {/* Form Body */}
            <form onSubmit={handleSaveInfoDasar} className="p-5 overflow-y-auto space-y-5 flex-1">
              {/* Nama lengkap */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-500 block">Nama lengkap</label>
                <input
                  type="text"
                  placeholder="Nama lengkap"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full h-12 px-4 rounded-xl border border-[#E0E6ED] bg-white text-sm text-[#0E1726] font-medium focus:outline-none focus:border-[#FC480D] transition-colors"
                />
              </div>

              {/* Username */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-500 block">Username</label>
                <input
                  type="text"
                  placeholder="Username"
                  value={editUsername}
                  onChange={(e) => setEditUsername(e.target.value)}
                  className="w-full h-12 px-4 rounded-xl border border-[#E0E6ED] bg-white text-sm text-[#0E1726] font-medium focus:outline-none focus:border-[#FC480D] transition-colors"
                />
              </div>

              {/* Lokasi (Searchable from core.wilayah) */}
              <div className="space-y-2 relative">
                <label className="text-xs font-semibold text-gray-500 block">Lokasi</label>
                <div className="relative flex items-center">
                  <input
                    type="text"
                    placeholder="Cari Kota/Kabupaten..."
                    value={locationSearchQuery}
                    onChange={(e) => {
                      setLocationSearchQuery(e.target.value);
                      setEditLocationName(e.target.value);
                    }}
                    className="w-full h-12 pl-4 pr-10 rounded-xl border border-[#E0E6ED] bg-white text-sm text-[#0E1726] font-medium focus:outline-none focus:border-[#FC480D] transition-colors"
                  />
                  <div className="absolute right-3 text-gray-400 pointer-events-none">
                    {isSearchingLocation ? (
                      <Icon icon="ph:spinner-gap-bold" className="w-5 h-5 animate-spin text-[#FC480D]" />
                    ) : (
                      <Icon icon="ph:caret-down-bold" className="w-4 h-4" />
                    )}
                  </div>
                </div>

                {/* Dropdown Options */}
                {locationOptions.length > 0 && (
                  <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white rounded-2xl border border-[#E0E6ED] shadow-xl max-h-48 overflow-y-auto divide-y divide-gray-100">
                    {locationOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setEditLocationCode(opt.value);
                          setEditLocationName(opt.nama);
                          setLocationSearchQuery(opt.nama);
                          setLocationOptions([]);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-orange-50 transition-colors text-xs font-medium text-[#0E1726]"
                      >
                        <div className="font-bold text-sm text-[#0E1726]">{opt.nama}</div>
                        <div className="text-gray-400 text-[11px] truncate">{opt.namaLengkap}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-3">
                <button
                  type="submit"
                  disabled={isSavingBasic}
                  className="w-full h-12 rounded-full bg-[#FC480D] hover:bg-[#D83B08] disabled:opacity-50 text-white font-bold text-sm shadow-md transition-all active:scale-98 cursor-pointer flex items-center justify-center gap-2"
                >
                  {isSavingBasic ? (
                    <>
                      <Icon icon="ph:spinner-gap-bold" className="w-5 h-5 animate-spin" />
                      <span>Menyimpan...</span>
                    </>
                  ) : (
                    <span>Simpan perubahan</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT INFO PEMAIN MODAL (MATCHING DESIGN IMPROVEMENT MOCKUP) */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col animate-in slide-in-from-bottom duration-300">
            {/* Header */}
            <div className="px-5 py-4 border-b border-[#E0E6ED] flex items-center gap-3 bg-white sticky top-0 z-10">
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-[#0E1726] hover:bg-gray-100 cursor-pointer transition-colors"
              >
                <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
              </button>
              <h2 className="text-base font-bold text-[#0E1726]">Edit Info Pemain</h2>
            </div>

            {/* Form Body */}
            <form onSubmit={handleSaveInfoPemain} className="p-5 overflow-y-auto space-y-5 flex-1">
              {/* Posisi / Nomor favorit */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-500 block">
                  {profileType === 'racket' ? 'Nomor favorit' : 'Posisi'}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(profileType === 'racket'
                    ? ['Tunggal', 'Ganda', 'Tunggal & Ganda']
                    : ['Defender', 'Midfielder', 'Forward', 'Goalkeeper']
                  ).map((pos) => {
                    const isSelected = editPosition === pos;
                    return (
                      <button
                        key={pos}
                        type="button"
                        onClick={() => setEditPosition(pos)}
                        className={`py-3 px-2 rounded-xl text-xs sm:text-sm font-semibold transition-all cursor-pointer border text-center ${
                          isSelected
                            ? profileType === 'racket'
                              ? 'bg-white text-[#1D4ED8] border-2 border-[#1D4ED8] shadow-2xs font-bold'
                              : 'bg-white text-[#FC480D] border-2 border-[#FC480D] shadow-2xs font-bold'
                            : 'bg-white text-[#0E1726] border-[#E0E6ED] hover:bg-gray-50'
                        }`}
                      >
                        {pos}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tanggal lahir */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-500 block">Tanggal lahir</label>
                <div className="relative flex items-center">
                  <input
                    type="date"
                    value={editBirthDate}
                    onChange={(e) => setEditBirthDate(e.target.value)}
                    className={`w-full h-12 pl-4 pr-10 rounded-xl border border-[#E0E6ED] bg-white text-sm text-[#0E1726] font-medium focus:outline-none transition-colors ${
                      profileType === 'racket' ? 'focus:border-[#1D4ED8]' : 'focus:border-[#FC480D]'
                    }`}
                  />
                  <Icon icon="ph:calendar-blank-bold" className="w-5 h-5 text-gray-400 absolute right-3 pointer-events-none" />
                </div>
              </div>

              {/* Kaki / Tangan dominan */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-500 block">
                  {profileType === 'racket' ? 'Tangan dominan' : 'Kaki dominan'}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(profileType === 'racket'
                    ? ['Kanan', 'Kiri', 'Keduanya']
                    : ['Kiri', 'Kanan', 'Keduanya']
                  ).map((foot) => {
                    const isSelected = editDominantFoot === foot;
                    return (
                      <button
                        key={foot}
                        type="button"
                        onClick={() => setEditDominantFoot(foot)}
                        className={`py-3 px-2 rounded-xl text-xs sm:text-sm font-semibold transition-all cursor-pointer border text-center ${
                          isSelected
                            ? profileType === 'racket'
                              ? 'bg-white text-[#1D4ED8] border-2 border-[#1D4ED8] shadow-2xs font-bold'
                              : 'bg-white text-[#FC480D] border-2 border-[#FC480D] shadow-2xs font-bold'
                            : 'bg-white text-[#0E1726] border-[#E0E6ED] hover:bg-gray-50'
                        }`}
                      >
                        {foot}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tinggi badan */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-500 block">Tinggi badan</label>
                <div className="relative flex items-center">
                  <input
                    type="number"
                    placeholder="176"
                    value={editHeight}
                    onChange={(e) => setEditHeight(e.target.value)}
                    className={`w-full h-12 pl-4 pr-12 rounded-xl border border-[#E0E6ED] bg-white text-sm text-[#0E1726] font-medium focus:outline-none transition-colors ${
                      profileType === 'racket' ? 'focus:border-[#1D4ED8]' : 'focus:border-[#FC480D]'
                    }`}
                  />
                  <span className="absolute right-4 text-xs font-semibold text-gray-400 pointer-events-none">
                    cm
                  </span>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-3">
                <button
                  type="submit"
                  disabled={isSaving}
                  className={`w-full h-12 rounded-full text-white font-bold text-sm shadow-md transition-all active:scale-98 cursor-pointer flex items-center justify-center gap-2 ${
                    profileType === 'racket'
                      ? 'bg-[#1D4ED8] hover:bg-[#1E40AF] disabled:bg-[#1D4ED8]/50'
                      : 'bg-[#FC480D] hover:bg-[#D83B08] disabled:bg-[#FC480D]/50'
                  }`}
                >
                  {isSaving ? (
                    <>
                      <Icon icon="ph:spinner-gap-bold" className="w-5 h-5 animate-spin" />
                      <span>Menyimpan...</span>
                    </>
                  ) : (
                    <span>Simpan perubahan</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT FOTO PROFIL MODAL */}
      {isAvatarModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
            {/* Top Bar Header */}
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setIsAvatarModalOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-700 transition-colors cursor-pointer"
              >
                <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
              </button>
              <h3 className="text-base font-bold text-[#0E1726]">Edit Foto Profil</h3>
            </div>

            {/* Content Body */}
            <div className="p-6 flex flex-col items-center justify-center overflow-y-auto space-y-4">
              {/* Image Preview Box with Rounded Square Orange Ring */}
              <div className="p-1.5 rounded-[32px] border-2 border-[#FC480D] bg-white w-64 h-64 shadow-xs flex items-center justify-center shrink-0">
                <img
                  src={previewAvatarUrl || avatarUrl}
                  alt="Preview Profil"
                  className="w-full h-full object-cover rounded-[24px] bg-gray-100"
                />
              </div>

              {/* Subtext */}
              <p className="text-xs text-gray-400 font-medium text-center">
                Rasio 1:1 · geser dan perbesar untuk crop
              </p>

              {/* Upload Button */}
              <label className="h-10 px-5 rounded-full border border-gray-200 bg-white text-xs font-bold text-[#0E1726] hover:bg-gray-50 flex items-center justify-center gap-2 cursor-pointer shadow-2xs transition-colors">
                <Icon icon="ph:upload-simple-bold" className="w-4 h-4 text-gray-700" />
                <span>Unggah foto baru</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/*"
                  onChange={handleSelectAvatarFile}
                  className="hidden"
                />
              </label>
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-gray-100 bg-white">
              <button
                type="button"
                onClick={handleSaveAvatar}
                disabled={isSavingAvatar || !newAvatarFile}
                className="w-full h-12 rounded-2xl bg-[#FC480D] hover:bg-[#D83B08] disabled:opacity-50 text-white text-sm font-bold flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-md"
              >
                {isSavingAvatar ? (
                  <>
                    <Icon icon="ph:spinner-gap-bold" className="w-5 h-5 animate-spin" />
                    <span>Menyimpan...</span>
                  </>
                ) : (
                  <span>Simpan foto</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PROFILE TYPE SWITCH CONFIRMATION BOTTOM SHEET MODAL */}
      {isSwitchModalOpen && targetProfileType && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden p-6 space-y-5 animate-in slide-in-from-bottom duration-300">
            {/* Top Drag Pill Handle */}
            <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto" />

            {/* Icon Header */}
            <div className="flex items-center gap-3.5">
              <div
                className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${
                  targetProfileType === 'racket'
                    ? 'bg-emerald-50 text-emerald-600'
                    : 'bg-orange-50 text-[#FC480D]'
                }`}
              >
                <Icon
                  icon={targetProfileType === 'racket' ? 'ph:tennis-ball-bold' : 'ph:soccer-ball-bold'}
                  className="w-7 h-7"
                />
              </div>

              <div>
                <h3 className="text-lg font-bold text-[#0E1726]">
                  Ganti ke {targetProfileType === 'racket' ? 'Racket Game' : 'Invasion Game'}?
                </h3>
                <p className="text-xs text-gray-400 font-medium">
                  Profil utama kamu akan berubah
                </p>
              </div>
            </div>

            {/* Body Explanation */}
            <p className="text-xs sm:text-sm text-gray-500 leading-relaxed font-normal">
              Profil yang dilihat pemain lain akan menampilkan data{' '}
              {targetProfileType === 'racket' ? 'Racket Game' : 'Invasion Game'} — level, statistik,
              riwayat event, dan pencapaian sesuai kategori ini. Kamu bisa menggantinya lagi kapan saja.
            </p>

            {/* Action Buttons */}
            <div className="space-y-2.5 pt-2">
              <button
                type="button"
                onClick={handleConfirmSwitchProfile}
                disabled={isSwitchingProfile}
                className="w-full h-12 rounded-full bg-[#FC480D] hover:bg-[#D83B08] disabled:opacity-50 text-white font-bold text-sm shadow-md transition-all active:scale-98 cursor-pointer flex items-center justify-center gap-2"
              >
                {isSwitchingProfile ? (
                  <>
                    <Icon icon="ph:spinner-gap-bold" className="w-5 h-5 animate-spin" />
                    <span>Memproses...</span>
                  </>
                ) : (
                  <>
                    <Icon icon="ph:check-bold" className="w-4 h-4" />
                    <span>
                      Ya, tampilkan {targetProfileType === 'racket' ? 'Racket Game' : 'Invasion Game'}
                    </span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsSwitchModalOpen(false);
                  setTargetProfileType(null);
                }}
                className="w-full h-12 rounded-full bg-[#F4F5F7] hover:bg-gray-200 text-[#0E1726] font-bold text-sm transition-colors cursor-pointer flex items-center justify-center"
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PHOTO GALLERY MODAL */}
      <PhotoGalleryModal
        open={galleryPreviewOpen}
        onOpenChange={setGalleryPreviewOpen}
        photos={galleryPhotos}
        initialIndex={galleryPreviewIndex}
        title={`Galeri ${displayName}`}
      />
    </div>
  );
};

export default ClientProfilePage;
